// import { useState } from 'react';
import { Button } from '@headlessui/react';
import Card from '../layouts/CardLayout';
import Input from './Input';
import PageHeader from './PageHeader';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  DEFAULT_DATA,
  PDFStaticBuilder,
  PDFStaticBuilderProps,
} from '../common/helpers/pdf-builder';
import { useEffect, useState } from 'react';
import FormTextarea from './FormTextarea';
import CertificateInformationModal from './CertificateInformationModal';
import {
  useClientCertificateQuery,
  useUpdateClientCertificateMutation,
} from '../services/certificate/certificate.service';
import { useParams } from 'react-router-dom';
import { useErrorToast } from '../hooks/useToast';
import * as Sentry from '@sentry/react';
import { getPDFGenerator } from '../common/helpers/pdf-generator';
import ConfirmationModal from './ConfirmationModal';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
  useCertificateTemplateQuery,
  useUpdateOrCreateCertificateTemplateMutation,
} from '../services/template/template.service';
import { useClientQuery } from '../services/clients/client.service';
import LoadingContent from './LoadingContent';

export const CertificateConfigurator = () => {
  const { clientId } = useParams();
  const [pdf, setPdf] = useState(null);
  const [isInformationOpen, setIsInformationOpen] = useState(false);
  const [isConfirmTemplateModalOpen, setIsConfirmTemplateModalOpen] = useState(false);
  const [confirmInitializeTemplate, setConfirmInitializeTemplate] = useState(false);

  const { mutate: saveConfig } = useUpdateClientCertificateMutation();
  const { data: certificateData, isLoading: isLoadingCertificate } =
    useClientCertificateQuery(clientId);
  const { data: client } = useClientQuery(clientId);
  const {
    data: template,
    error: templateError,
    refetch: refetchTemplate,
    isLoading: isLoadingTemplate,
  } = useCertificateTemplateQuery(client?.company_id, true);
  const { mutateAsync: saveTemplate } = useUpdateOrCreateCertificateTemplateMutation();

  useEffect(() => {
    if (certificateData) {
      reset(certificateData?.config);
      onSubmit(certificateData?.config);
    }
  }, [certificateData]);

  useEffect(() => {
    if (templateError) {
      useErrorToast(
        'Eroare la incarcarea sablonului. Asigura-te ca ai setat un sablon pentru clientii tai.',
      );
    }
  }, [templateError]);

  useEffect(() => {
    if (template) {
      reset(template?.config);
      onSubmit(template?.config);
    }
  }, [template]);

  const onSave = (data: PDFStaticBuilderProps) => {
    if (clientId) {
      onSubmit(data);
      saveConfig(
        { clientCertificateId: undefined, client_id: +clientId, config: data },
        { onError: () => useErrorToast('Eroare la salvarea modelului adeverintei') },
      );
    }
  };

  const { handleSubmit, control, reset } = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_DATA,
  });

  const { fields: footerFields, append: appendFooterFields } = useFieldArray({
    control,
    name: 'footer',
  });

  const { fields: clientInformations, append: appendClientInformation } = useFieldArray({
    control,
    name: 'first_page.client_information',
  });

  const { fields: firstPageParagraphs, append: appendFirstPageParagraphs } = useFieldArray({
    control,
    name: 'first_page.paragraphs',
  });

  const { fields: afterTablesParagraphs, append: appendAfterTablesParagraphs } = useFieldArray({
    control,
    name: 'after_tables',
  });

  const { fields: lastPageParagraphs, append: appendLastPageParagraphs } = useFieldArray({
    control,
    name: 'last_page',
  });

  const onAddFooterField = () => {
    if (footerFields.length < 5) {
      appendFooterFields('');
    }
  };

  const onAddClientInformation = () => {
    if (clientInformations.length < 4) {
      appendClientInformation('');
    }
  };

  const onAddFirstPageParagraph = () => {
    if (firstPageParagraphs.length < 5) {
      appendFirstPageParagraphs('');
    }
  };

  const onAddAfterTablesParagraph = () => {
    if (afterTablesParagraphs.length < 5) {
      appendAfterTablesParagraphs('');
    }
  };

  const onAddLastPageParagraph = () => {
    if (lastPageParagraphs.length < 7) {
      appendLastPageParagraphs('');
    }
  };

  const onSubmit = (data: any) => {
    const configData = structuredClone(data);
    const config = PDFStaticBuilder(configData);
    try {
      const pdfGenerator = getPDFGenerator(config);
      pdfGenerator.getBase64((base64) => {
        setPdf(base64);
      });
    } catch (e) {
      useErrorToast('Eroare la generarea PDF-ului');
      Sentry.captureException(e);
    }
  };

  const setConfirmTemplateModalOpen = () => {
    setIsConfirmTemplateModalOpen(true);
  };

  const setAsStandardTemplate = (data: PDFStaticBuilderProps) => {
    if (client?.company_id) {
      saveTemplate(
        { company_id: client?.company_id, config: data },
        {
          onSuccess: () => {
            setIsConfirmTemplateModalOpen(false);
          },
          onError: (error) => {
            useErrorToast('Eroare la salvarea sablonului');
            Sentry.captureException(error);
          },
        },
      );
    }
  };

  const confirmSaveTemplate = () => {
    handleSubmit(setAsStandardTemplate)();
  };

  const onGetTemplate = () => {
    if (client?.company_id) {
      refetchTemplate();
    }

    setConfirmInitializeTemplate(false);
  };

  if (isLoadingCertificate || isLoadingTemplate) {
    return <LoadingContent />;
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between w-full">
        <div className="flex gap-4">
          <PageHeader title="Setari adeverinta" />
          <Button className={'btn-outline-secondary'} onClick={() => setIsInformationOpen(true)}>
            Informatii
          </Button>
          <Button
            className={'btn-outline-secondary'}
            onClick={() => setConfirmInitializeTemplate(true)}
          >
            Utilizare sablon
          </Button>
          <Button className={'btn-outline-secondary'} onClick={handleSubmit(onSubmit)}>
            Actualizeaza adeverinta
          </Button>
        </div>
        <div className="flex gap-4">
          <Button className={'btn-outline-secondary'} onClick={setConfirmTemplateModalOpen}>
            Seteaza ca sablon reutilizabil
          </Button>
          <Button className={'btn-primary'} onClick={handleSubmit(onSave)}>
            Salveaza model adeverinta
          </Button>
        </div>
      </div>
      <div className="flex w-full gap-4">
        <div className="flex w-[70%] flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex w-full">
              <Card>
                <div className="p-4 flex flex-col gap-4">
                  <p className="text-xl">Antet</p>
                  <div className="flex flex-col gap-4">
                    <p className="text-sm italic">
                      Imaginea pentru logo trebuie incarcata separat pe site-ul:{' '}
                      <a
                        className="text-sm"
                        href="https://postimages.org"
                        target="_blank"
                        rel="noreferrer"
                      >
                        postimages.org
                      </a>{' '}
                      si utilizat link-ul direct (direct link). Doar formatele: .jpeg / .jpg / .png
                    </p>
                    <Controller
                      name="header.image"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Input type="text" onChange={onChange} value={value} label="Logo" />
                      )}
                    />
                    <hr />
                    <Controller
                      name="header.rightText"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Input type="text" onChange={onChange} value={value} label="Text Dreapta" />
                      )}
                    />
                  </div>
                </div>
              </Card>
            </div>
            <div className="flex w-full">
              <Card>
                <div className="p-4 flex flex-col gap-4">
                  <p className="text-xl">Subsol</p>
                  <div className="flex flex-col">
                    <p className="text-sm italic">Textele de subsol sunt centrate.</p>
                    <p className="text-sm italic">Numerotarea paginilor este adaugata automat.</p>
                  </div>
                  <div className="flex flex-col gap-4">
                    {footerFields?.map((field: any, index: number) => (
                      <div className="w-full" key={`${field.id}`}>
                        <Controller
                          name={`footer.${index}`}
                          key={`${field.id}`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Input
                              type="text"
                              key={field.id}
                              placeholder={'Text de subsol'}
                              onChange={onChange}
                              value={value}
                            />
                          )}
                        />
                      </div>
                    ))}
                    <Button className="btn-primary w-full" onClick={onAddFooterField}>
                      Adauga camp
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          <div className="flex gap-2">
            <Card>
              <div className="p-4 flex flex-col gap-4">
                <p className="text-xl">Caracter special luni necompletate</p>
                <Controller
                  name={`settings.empty_months`}
                  key={`setting.empty_months_value`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      key={`setting.empty_months_value`}
                      placeholder={'0'}
                      defaultValue={'0'}
                      onChange={onChange}
                      maxLength={1}
                      value={value}
                    />
                  )}
                />
              </div>
            </Card>
          </div>
          <div className="flex gap-2 flex-col">
            <Card>
              <div className="p-4 flex flex-col gap-4">
                <p className="text-xl">Prima pagina</p>
                <p className="text-sm italic">Informatii client (stanga-sus)</p>
                <div className="flex flex-col gap-4">
                  {clientInformations?.map((field: any, index: number) => (
                    <div className="w-full" key={`${field.id}`}>
                      <Controller
                        name={`first_page.client_information.${index}`}
                        key={`${field.id}_value`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Input
                            type="text"
                            key={field.id}
                            placeholder={'Text adresa client'}
                            onChange={onChange}
                            value={value}
                          />
                        )}
                      />
                    </div>
                  ))}
                  <Button className="btn-primary w-full" onClick={onAddClientInformation}>
                    Adauga camp
                  </Button>
                </div>
                <hr />
                <Controller
                  name="first_page.title"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      onChange={onChange}
                      value={value}
                      label="Titlul adeverintei"
                    />
                  )}
                />
                <hr />
                <div className="text-sm italic flex flex-col gap-2">
                  <p className="text-md not-italic">Continut</p>
                </div>
                <div className="flex flex-col gap-4">
                  {firstPageParagraphs?.map((field: any, index: number) => (
                    <div className="w-full" key={`${field.id}`}>
                      <Controller
                        name={`first_page.paragraphs.${index}.text`}
                        key={`${field.id}`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <FormTextarea
                            key={field.id}
                            placeholder={'Paragraf'}
                            onChange={onChange}
                            value={value}
                            label={`Paragraf ${index + 1}`}
                          />
                        )}
                      />
                      <div className="flex gap-4">
                        <Controller
                          name={`first_page.paragraphs.${index}.alignment`}
                          control={control}
                          defaultValue={'left'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Aliniere:</p>
                              <Button
                                onClick={() => onChange('left')}
                                className={value === 'left' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Stanga
                              </Button>
                              <Button
                                onClick={() => onChange('center')}
                                className={value == 'center' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Centru
                              </Button>
                              <Button
                                onClick={() => onChange('right')}
                                className={value === 'right' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Dreapta
                              </Button>
                            </div>
                          )}
                        />
                        <Controller
                          name={`first_page.paragraphs.${index}.type`}
                          control={control}
                          defaultValue={'text'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Tip:</p>
                              <Button
                                onClick={() => onChange('text')}
                                className={value === 'text' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Text
                              </Button>
                              <Button
                                onClick={() => onChange('html')}
                                className={value === 'html' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                HTML
                              </Button>
                            </div>
                          )}
                        />
                      </div>
                      <hr className="mt-2" />
                    </div>
                  ))}
                  <Button className="btn-primary w-full" onClick={onAddFirstPageParagraph}>
                    Adauga paragraf
                  </Button>
                </div>
              </div>
            </Card>
          </div>
          <div className="flex gap-2 flex-col">
            <Card>
              <div className="p-4 flex flex-col gap-4">
                <div className="text-sm italic flex flex-col gap-2">
                  <p className="text-md not-italic">Text dupa tabele</p>
                </div>
                <div className="flex flex-col gap-4">
                  {afterTablesParagraphs?.map((field: any, index: number) => (
                    <div className="w-full" key={`${field.id}`}>
                      <Controller
                        name={`after_tables.${index}.text`}
                        key={`${field.id}`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <FormTextarea
                            key={field.id}
                            placeholder={'Paragraf'}
                            onChange={onChange}
                            value={value}
                            label={`Paragraf ${index + 1}`}
                          />
                        )}
                      />
                      <div className="flex gap-4">
                        <Controller
                          name={`after_tables.${index}.alignment`}
                          control={control}
                          defaultValue={'left'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Aliniere:</p>
                              <Button
                                onClick={() => onChange('left')}
                                className={value === 'left' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Stanga
                              </Button>
                              <Button
                                onClick={() => onChange('center')}
                                className={value == 'center' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Centru
                              </Button>
                              <Button
                                onClick={() => onChange('right')}
                                className={value === 'right' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Dreapta
                              </Button>
                            </div>
                          )}
                        />
                        <Controller
                          name={`after_tables.${index}.type`}
                          control={control}
                          defaultValue={'text'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Tip:</p>
                              <Button
                                onClick={() => onChange('text')}
                                className={value === 'text' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Text
                              </Button>
                              <Button
                                onClick={() => onChange('html')}
                                className={value === 'html' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                HTML
                              </Button>
                            </div>
                          )}
                        />
                      </div>
                      <hr className="mt-2" />
                    </div>
                  ))}
                  <Button className="btn-primary w-full" onClick={onAddAfterTablesParagraph}>
                    Adauga paragraf
                  </Button>
                </div>
              </div>
            </Card>
          </div>
          <div className="flex gap-2 flex-col">
            <Card>
              <div className="p-4 flex flex-col gap-4">
                <div className="text-sm italic flex flex-col gap-2">
                  <p className="text-md not-italic">Ultima pagina</p>
                </div>
                <div className="flex flex-col gap-4">
                  {lastPageParagraphs?.map((field: any, index: number) => (
                    <div className="w-full" key={`${field.id}`}>
                      <Controller
                        name={`last_page.${index}.text`}
                        key={`${field.id}`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <FormTextarea
                            key={field.id}
                            placeholder={'Paragraf'}
                            onChange={onChange}
                            value={value}
                            label={`Paragraf ${index + 1}`}
                          />
                        )}
                      />
                      <div className="flex gap-4">
                        <Controller
                          name={`last_page.${index}.alignment`}
                          control={control}
                          defaultValue={'left'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Aliniere:</p>
                              <Button
                                onClick={() => onChange('left')}
                                className={value === 'left' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Stanga
                              </Button>
                              <Button
                                onClick={() => onChange('center')}
                                className={value == 'center' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Centru
                              </Button>
                              <Button
                                onClick={() => onChange('right')}
                                className={value === 'right' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Dreapta
                              </Button>
                            </div>
                          )}
                        />
                        <Controller
                          name={`last_page.${index}.type`}
                          control={control}
                          defaultValue={'text'}
                          render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2 items-center">
                              <p>Tip:</p>
                              <Button
                                onClick={() => onChange('text')}
                                className={value === 'text' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                Text
                              </Button>
                              <Button
                                onClick={() => onChange('html')}
                                className={value === 'html' ? 'btn-selected' : 'btn-not-selected'}
                              >
                                HTML
                              </Button>
                            </div>
                          )}
                        />
                      </div>
                      <hr className="mt-2" />
                    </div>
                  ))}
                  <Button className="btn-primary w-full" onClick={onAddLastPageParagraph}>
                    Adauga paragraf
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="flex w-full h-[120rem]">
          <iframe
            className="w-full rounded-xl"
            src={pdf ? `data:application/pdf;base64,${pdf}` : ''}
          />
        </div>
      </div>
      {isInformationOpen && (
        <CertificateInformationModal onClose={() => setIsInformationOpen(false)} />
      )}
      {isConfirmTemplateModalOpen && (
        <ConfirmationModal
          title="Setare sablon reutilizabil"
          description="Esti sigur ca vrei sa setezi acest model ca sablon reutilizabil pentru clientii tai?"
          onClose={() => setIsConfirmTemplateModalOpen(false)}
          icon={<ExclamationCircleIcon className="text-yellow h-16 w-14" />}
          onConfirm={confirmSaveTemplate}
          confirmButtonClass="btn-primary"
          confirmButtonLabel="Confirma"
        />
      )}
      {confirmInitializeTemplate && (
        <ConfirmationModal
          title="Utilizare sablon"
          description="Esti sigur ca vrei sa initializezi sablonul? Vei pierde modificarile nesalvate."
          onClose={() => setConfirmInitializeTemplate(false)}
          icon={<ExclamationCircleIcon className="text-yellow h-16 w-14" />}
          onConfirm={onGetTemplate}
          confirmButtonClass="btn-primary"
          confirmButtonLabel="Confirma"
        />
      )}
    </div>
  );
};
